import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { VALUE_LINE_HEIGHT } from "web_myplann/Shared/Components/FlatRow/constants";
import { FlatRowSelectOptionList } from "web_myplann/Shared/Components/FlatRow/FlatRowSelectOptionList";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

export type FlatRowSelectOption<TValue extends string | number = string> = {
	value: TValue;
	label: string;
};

export type FlatRowSelectProps<TValue extends string | number = string> = {
	label: string;
	value: TValue | "";
	options: FlatRowSelectOption<TValue>[];
	onChange: (value: TValue) => void;
	onBlur?: () => void;
	drawerTitle?: string;
	isLast?: boolean;
	error?: boolean;
	helperText?: string;
};

export const FlatRowSelect = <TValue extends string | number = string>({
	label,
	value,
	options,
	onChange,
	onBlur,
	drawerTitle,
	isLast,
	error,
	helperText,
}: FlatRowSelectProps<TValue>) => {
	const theme = useTheme();
	const [open, setOpen] = useState(false);

	const selectedLabel =
		options.find((option) => String(option.value) === String(value))?.label ??
		"";

	const handleSelect = (optionValue: TValue) => {
		onChange(optionValue);
		setOpen(false);
	};

	const closeDrawer = () => {
		setOpen(false);
		onBlur?.();
	};

	return (
		<Box borderBottom={isLast ? "none" : theme.colors.border.flatRowDivider}>
			<ButtonBase
				onClick={() => setOpen(true)}
				sx={{
					width: "100%",
					px: 1.6,
					py: 1.4,
					minHeight: 48,
					justifyContent: "space-between",
					alignItems: "flex-start",
					"&:active": { bgcolor: theme.palette.deprecated.grey[50] },
				}}
			>
				<Typography
					variant="deprecatedBody1"
					color={
						error
							? theme.palette.deprecated.error.main
							: theme.palette.deprecated.grey[500]
					}
					flexShrink={0}
					mr={2}
					lineHeight={`${VALUE_LINE_HEIGHT}px`}
				>
					{label}
				</Typography>
				<Typography
					variant="deprecatedBodyMedium"
					color={theme.palette.text.primary}
					sx={{
						flex: 1,
						minWidth: 0,
						textAlign: "right",
						lineHeight: `${VALUE_LINE_HEIGHT}px`,
						overflowWrap: "anywhere",
					}}
				>
					{selectedLabel}
				</Typography>
			</ButtonBase>

			{error && helperText && (
				<Typography
					component="p"
					variant="deprecatedCaption"
					color={theme.palette.deprecated.error.main}
					px={1.6}
					pb={0.6}
				>
					{helperText}
				</Typography>
			)}

			<MyPlannDrawer
				anchor="bottom"
				open={open}
				onClose={closeDrawer}
				title={drawerTitle ?? label}
				closeButton
			>
				<FlatRowSelectOptionList
					options={options}
					value={value}
					onSelect={handleSelect}
				/>
			</MyPlannDrawer>
		</Box>
	);
};
